// store/features/user-preferences.feature.tsx
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';
import { isRecord } from '@/lib/is-record';

export interface UserPreferencesResponse {
  user_id: string;
  preferences: Record<string, unknown>;
  created_at?: string;
  modified_at?: string;
}

/**
 * A preference after a PUT, as the server stores it: an object merges one
 * level into the object saved before, so a write of one entry keeps the
 * others; anything else replaces the value.
 */
export function mergePreferenceValue(saved: unknown, value: unknown): unknown {
  return isRecord(saved) && isRecord(value) ? { ...saved, ...value } : value;
}

export const userPreferencesApi = createApi({
  reducerPath: 'userPreferencesApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['UserPreferences'],
  // Disable automatic refetching - preferences are stable and only change via user actions
  refetchOnFocus: false,
  refetchOnReconnect: false,
  refetchOnMountOrArgChange: false,
  endpoints: (builder) => ({
    // Get all user preferences - fetched once at app start, cached indefinitely
    getUserPreferences: builder.query<UserPreferencesResponse, void>({
      query: () => '/user-preferences',
      providesTags: ['UserPreferences'],
      // Keep cached data for entire session (1 hour)
      keepUnusedDataFor: 3600,
    }),

    // Uses optimistic update - no refetch needed
    setUserPreference: builder.mutation<
      UserPreferencesResponse,
      { key: string; value: unknown }
    >({
      query: ({ key, value }) => ({
        url: `/user-preferences/${key}`,
        method: 'PUT',
        body: { value },
      }),
      // Optimistic update - update cache directly without refetching
      async onQueryStarted({ key, value }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          userPreferencesApi.util.updateQueryData(
            'getUserPreferences',
            undefined,
            (draft) => {
              draft.preferences[key] = mergePreferenceValue(
                draft.preferences[key],
                value
              );
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          // Revert on error
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useGetUserPreferencesQuery,
  useLazyGetUserPreferencesQuery,
  useSetUserPreferenceMutation,
} = userPreferencesApi;
